/**
 * @fileoverview Local-first background jobs service — powers the tether
 *               indicator flyout in system-web and tenant-web. Reads from
 *               the synced Dexie `background_jobs` table; never calls the
 *               API directly. Progress for long-running worker jobs (ICC
 *               code set downloads, AI embedding, import runs, etc.) is
 *               written by workers to Mongo `backgroundJobs`, streamed to
 *               clients via the existing Mongo change-stream → WebSocket
 *               sync channel, and upserted into Dexie automatically.
 * @module local-db/services/background-jobs
 *
 * Responsibilities:
 * - Expose reactive `liveQuery` observables over the `background_jobs` Dexie table.
 * - Classify jobs into "active" vs "recently finished" for flyout rendering.
 * - Store UI-only failure dismissals in `_sync_meta` without mutating server-owned jobs.
 * - Provide plain-array readers for tests and non-reactive callers.
 *
 * Dependencies:
 * - dexie (liveQuery)
 * - A Dexie-opened instance containing a `background_jobs` table (system or tenant DB).
 *
 * Architecture:
 * - Local-first: Dexie is the source of truth for the UI. API calls are
 *   intentionally absent — the server-side change-stream keeps Dexie fresh.
 * - `background_jobs` rows remain server-authoritative; local clear actions
 *   only write dismissal metadata into `_sync_meta`.
 * - Works identically against `getSystemDb()` (system-web) and
 *   `getTenantDb()` (tenant-web). Callers inject the opened DB instance.
 *
 * Notes:
 * - Server-authoritative collection: clients NEVER push to `background_jobs`.
 *   The absence of `backgroundJobs` from SYSTEM/TENANT_PUSH_ALLOWED_COLLECTIONS
 *   enforces this at the sync layer.
 */

import Dexie, { liveQuery, type Observable } from 'dexie';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/**
 * @contract BackgroundJobStatus
 *
 * Purpose:
 * - Enumerates the lifecycle states of a background worker job surfaced to the UI.
 *
 * Fields:
 * - pending:    Enqueued but not yet picked up by a worker.
 * - running:    Worker has started processing.
 * - completed:  Finished successfully.
 * - failed:     Finished with an error.
 * - cancelled:  User-cancelled or superseded.
 *
 * Rules:
 * - Mirrors the `status` field written by workers to Mongo `backgroundJobs`.
 *
 * Sync:
 * - Server-authoritative; client never mutates.
 */
export type BackgroundJobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

/**
 * @contract BackgroundJobScope
 *
 * Purpose:
 * - Scope key for filtering jobs into the correct app's flyout.
 *
 * Fields:
 * - system: System-wide job (visible in system-web).
 * - tenant: Tenant-scoped job (visible in tenant-web; `tenantId` set).
 *
 * Rules:
 * - Defaults to `system` when unset.
 *
 * Sync:
 * - Written by workers; read-only on clients.
 */
export type BackgroundJobScope = 'system' | 'tenant';

/**
 * @contract BackgroundJobProgress
 *
 * Purpose:
 * - Structured progress payload for long-running jobs.
 *
 * Fields:
 * - percent: 0-100 progress indicator (optional — omit if unknown).
 * - current: Number of units processed.
 * - total:   Total units expected (optional — omit when unknown).
 * - unit:    Human-readable unit label (e.g. "chapters", "violations").
 * - phase:   Short machine phase key (e.g. "token", "catalog", "hydrate").
 *
 * Rules:
 * - Workers SHOULD update at least every 5s during the running phase.
 *
 * Sync:
 * - Updated via Mongo change stream → WS → Dexie.
 */
export interface BackgroundJobProgress {
  percent?: number;
  current?: number;
  total?: number;
  unit?: string;
  phase?: string;
}

/**
 * @contract BackgroundJob
 *
 * Purpose:
 * - Canonical shape of a row in the Dexie `background_jobs` table.
 *
 * Fields:
 * - _id:        Server-generated job identifier.
 * - jobType:    Canonical job type key (e.g. "codeSetDownload", "aiEmbedCollection").
 * - scope:      system | tenant.
 * - tenantId:   Tenant slug / id for `tenant` scope (optional for system).
 * - status:     Lifecycle status.
 * - title:      User-facing title ("Downloading IFC 2024").
 * - subtitle:   Optional secondary line (e.g. vendor name + book id).
 * - progress:   Structured progress object.
 * - message:    Human-readable current message (overrides `progress.phase` in UI).
 * - error:      Error string when `status === 'failed'`.
 * - payload:    Free-form payload with job-specific metadata (bookId, etc.).
 * - startedAt:  ISO-8601 timestamp when the worker picked up the job.
 * - updatedAt:  Sync cursor timestamp.
 * - completedAt: ISO-8601 timestamp when terminal state was reached.
 *
 * Rules:
 * - `_id`, `jobType`, `scope`, `status`, and `startedAt` are required.
 * - All other fields are optional and evolve over the job lifecycle.
 *
 * Sync:
 * - Read-only from the client. Workers own all writes.
 */
export interface BackgroundJob {
  _id: string;
  jobType: string;
  scope: BackgroundJobScope;
  tenantId?: string;
  status: BackgroundJobStatus;
  title: string;
  subtitle?: string;
  progress?: BackgroundJobProgress;
  message?: string;
  error?: string;
  payload?: Record<string, unknown>;
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
}

/**
 * @contract BackgroundJobFailureDismissalState
 *
 * Purpose:
 * - Local-only UI state for failed background jobs the user has cleared from
 *   the bottom tether indicator.
 *
 * Fields:
 * - _id: Static `_sync_meta` key for the dismissal record.
 * - jobIds: Failed background job ids hidden from the tether.
 * - updatedAt: ISO timestamp of the latest clear action.
 *
 * Rules:
 * - Must never be written to `background_jobs`.
 * - Only failed terminal jobs are filtered by this state.
 *
 * Sync:
 * - Local-only `_sync_meta`; not pushed to Mongo.
 */
export interface BackgroundJobFailureDismissalState {
  _id: string;
  jobIds: string[];
  updatedAt: string;
}

/**
 * @contract BackgroundJobsBundle
 *
 * Purpose:
 * - Precomputed view model for the tether flyout: separates active jobs
 *   (pending + running) from recently finished jobs (last N terminal).
 *
 * Fields:
 * - active:          Jobs with `status === 'pending' | 'running'`, oldest first.
 * - recentlyFinished: Jobs with terminal status, newest first, capped at `recentLimit`.
 * - activeCount:     Count of active jobs.
 * - hasFailures:     True if any job in either set is `failed`.
 *
 * Rules:
 * - Pure projection over the Dexie snapshot; no side effects.
 *
 * Sync:
 * - Derived from Dexie reads; refreshes automatically via liveQuery.
 */
export interface BackgroundJobsBundle {
  active: BackgroundJob[];
  recentlyFinished: BackgroundJob[];
  activeCount: number;
  hasFailures: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TERMINAL_STATUSES: ReadonlyArray<BackgroundJobStatus> = ['completed', 'failed', 'cancelled'];
const ACTIVE_STATUSES: ReadonlyArray<BackgroundJobStatus> = ['pending', 'running'];
const DEFAULT_RECENT_LIMIT = 10;

/**
 * @storage background_jobs
 *
 * Type:
 * - IndexedDB
 *
 * Schema:
 * - `_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt`
 *
 * Sync Role:
 * - Mirror of the server-authoritative `backgroundJobs` Mongo collection.
 */
type DexieLike = Dexie | { table: (name: string) => Dexie.Table<any, any> };
const FAILURE_DISMISSALS_META_ID = 'background-job-failure-dismissals';

function getTable(db: DexieLike): Dexie.Table<BackgroundJob, string> {
  return (db as any).table('background_jobs') as Dexie.Table<BackgroundJob, string>;
}

function getMetaTable(db: DexieLike): Dexie.Table<BackgroundJobFailureDismissalState, string> {
  return (db as any).table('_sync_meta') as Dexie.Table<BackgroundJobFailureDismissalState, string>;
}

async function listDismissedBackgroundFailureIds(db: DexieLike): Promise<Set<string>> {
  try {
    const row = await getMetaTable(db).get(FAILURE_DISMISSALS_META_ID);
    return new Set(Array.isArray(row?.jobIds) ? row.jobIds : []);
  } catch (error) {
    console.error('[backgroundJobService] listDismissedBackgroundFailureIds failed:', error);
    return new Set();
  }
}

// ---------------------------------------------------------------------------
// Readers
// ---------------------------------------------------------------------------

/**
 * @process ListActiveBackgroundJobs
 *
 * Steps:
 * 1. Open the `background_jobs` Dexie table.
 * 2. Return all rows whose `status` is pending or running, oldest startedAt first.
 *
 * Inputs:
 * - db: Opened Dexie database instance.
 *
 * Outputs:
 * - Plain-array snapshot of active jobs.
 *
 * Failure Handling:
 * - Swallows Dexie errors and returns [] so callers can render a safe UI.
 *
 * Idempotency:
 * - REQUIRED.
 */
export async function listActiveBackgroundJobs(db: DexieLike): Promise<BackgroundJob[]> {
  try {
    return await getTable(db)
      .where('status')
      .anyOf(ACTIVE_STATUSES as unknown as string[])
      .sortBy('startedAt');
  } catch (error) {
    console.error('[backgroundJobService] listActiveBackgroundJobs failed:', error);
    return [];
  }
}

/**
 * @process ListRecentlyFinishedBackgroundJobs
 *
 * Steps:
 * 1. Read the `background_jobs` table filtered to terminal statuses.
 * 2. Sort descending by `updatedAt`.
 * 3. Truncate to `limit`.
 *
 * Inputs:
 * - db: Opened Dexie database instance.
 * - limit: Max rows to return. Defaults to 10.
 *
 * Outputs:
 * - Newest-first array of recently finished jobs.
 *
 * Failure Handling:
 * - Returns [] on Dexie error.
 *
 * Idempotency:
 * - REQUIRED.
 */
export async function listRecentlyFinishedBackgroundJobs(
  db: DexieLike,
  limit: number = DEFAULT_RECENT_LIMIT,
): Promise<BackgroundJob[]> {
  try {
    const [terminalRows, dismissedFailureIds] = await Promise.all([
      getTable(db)
      .where('status')
      .anyOf(TERMINAL_STATUSES as unknown as string[])
      .toArray(),
      listDismissedBackgroundFailureIds(db),
    ]);
    const visibleRows = terminalRows.filter((job) => (
      job.status !== 'failed' || !dismissedFailureIds.has(job._id)
    ));
    visibleRows.sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
    return visibleRows.slice(0, limit);
  } catch (error) {
    console.error('[backgroundJobService] listRecentlyFinishedBackgroundJobs failed:', error);
    return [];
  }
}

/**
 * @process ClearBackgroundJobFailures
 *
 * Steps:
 * 1. Read failed terminal jobs from local Dexie `background_jobs`.
 * 2. Merge their ids into the local-only `_sync_meta` dismissal document.
 * 3. Let liveQuery recompute the tether bundle so dismissed failures disappear.
 *
 * Inputs:
 * - db: Opened Dexie database.
 *
 * Outputs:
 * - Number of failed rows included in the clear operation.
 *
 * Failure Handling:
 * - Logs Dexie errors and returns 0; no API call is attempted.
 *
 * Idempotency:
 * - REQUIRED. Clearing the same failure repeatedly leaves one dismissal id.
 */
export async function clearBackgroundJobFailures(db: DexieLike): Promise<number> {
  try {
    // Resolve the _sync_meta table handle ONCE. Dexie returns the same Table
    // instance on every call, but test mocks (and some wrappers) may return a
    // fresh object per call — using two different handles for the read and the
    // write loses the put spy in tests and can split state in edge cases.
    const metaTable = getMetaTable(db);
    const [failedRows, existingRow] = await Promise.all([
      getTable(db).where('status').equals('failed').toArray(),
      metaTable.get(FAILURE_DISMISSALS_META_ID),
    ]);
    if (failedRows.length === 0) {
      return 0;
    }
    const existingDismissedIds = new Set<string>(
      Array.isArray(existingRow?.jobIds) ? existingRow.jobIds : [],
    );
    for (const job of failedRows) {
      existingDismissedIds.add(job._id);
    }
    await metaTable.put({
      _id: FAILURE_DISMISSALS_META_ID,
      jobIds: Array.from(existingDismissedIds),
      updatedAt: new Date().toISOString(),
    });
    return failedRows.length;
  } catch (error) {
    console.error('[backgroundJobService] clearBackgroundJobFailures failed:', error);
    return 0;
  }
}

/**
 * @process GetBackgroundJobsBundle
 *
 * Steps:
 * 1. Load active jobs and recently-finished jobs in parallel.
 * 2. Compute `activeCount` and `hasFailures`.
 * 3. Return the bundle as a single snapshot.
 *
 * Inputs:
 * - db: Opened Dexie database.
 * - recentLimit: Optional cap on recently-finished list (default 10).
 *
 * Outputs:
 * - BackgroundJobsBundle snapshot.
 *
 * Failure Handling:
 * - Propagates nothing; child readers already swallow errors.
 *
 * Idempotency:
 * - REQUIRED.
 */
export async function getBackgroundJobsBundle(
  db: DexieLike,
  recentLimit: number = DEFAULT_RECENT_LIMIT,
): Promise<BackgroundJobsBundle> {
  const [active, recentlyFinished] = await Promise.all([
    listActiveBackgroundJobs(db),
    listRecentlyFinishedBackgroundJobs(db, recentLimit),
  ]);
  const hasFailures =
    active.some((j) => j.status === 'failed') || recentlyFinished.some((j) => j.status === 'failed');
  return {
    active,
    recentlyFinished,
    activeCount: active.length,
    hasFailures,
  };
}

/**
 * @process WatchBackgroundJobsBundle
 *
 * Steps:
 * 1. Build a Dexie `liveQuery` observable that recomputes the bundle
 *    whenever the `background_jobs` table changes.
 *
 * Inputs:
 * - db: Opened Dexie database.
 * - recentLimit: Optional cap on recently-finished list (default 10).
 *
 * Outputs:
 * - Dexie Observable emitting a fresh bundle on every change.
 *
 * Failure Handling:
 * - Errors surface through the observable's error channel.
 *
 * Idempotency:
 * - REQUIRED — safe to subscribe/unsubscribe repeatedly.
 */
export function watchBackgroundJobsBundle(
  db: DexieLike,
  recentLimit: number = DEFAULT_RECENT_LIMIT,
): Observable<BackgroundJobsBundle> {
  return liveQuery<BackgroundJobsBundle>(() => getBackgroundJobsBundle(db, recentLimit));
}

/**
 * @process WatchActiveBackgroundJobsByType
 *
 * Steps:
 * 1. Build a `liveQuery` observable that returns active jobs filtered by jobType.
 *    Useful for focused surfaces (e.g. the ICC Code Sets page showing only
 *    `codeSetDownload` jobs for the current department).
 *
 * Inputs:
 * - db: Opened Dexie database.
 * - jobType: The jobType to filter on.
 *
 * Outputs:
 * - Dexie Observable of active BackgroundJob[] matching the type.
 *
 * Failure Handling:
 * - Errors propagate through the observable.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function watchActiveBackgroundJobsByType(
  db: DexieLike,
  jobType: string,
): Observable<BackgroundJob[]> {
  return liveQuery<BackgroundJob[]>(async () => {
    try {
      const rows = await getTable(db)
        .where('jobType')
        .equals(jobType)
        .toArray();
      return rows
        .filter((j) => ACTIVE_STATUSES.includes(j.status))
        .sort((a, b) => (a.startedAt ?? '').localeCompare(b.startedAt ?? ''));
    } catch (error) {
      console.error('[backgroundJobService] watchActiveBackgroundJobsByType failed:', error);
      return [];
    }
  });
}

/**
 * @process FindBackgroundJobById
 *
 * Steps:
 * 1. Look up a single job by primary key `_id`.
 *
 * Inputs:
 * - db: Opened Dexie database.
 * - jobId: Primary key.
 *
 * Outputs:
 * - The job or null when missing.
 *
 * Failure Handling:
 * - Returns null on error.
 *
 * Idempotency:
 * - REQUIRED.
 */
export async function findBackgroundJobById(
  db: DexieLike,
  jobId: string,
): Promise<BackgroundJob | null> {
  try {
    const row = await getTable(db).get(jobId);
    return (row as BackgroundJob | undefined) ?? null;
  } catch (error) {
    console.error('[backgroundJobService] findBackgroundJobById failed:', error);
    return null;
  }
}

/**
 * @process WatchBackgroundJobById
 *
 * Steps:
 * 1. Build a Dexie `liveQuery` observable that emits the single job document
 *    whenever the `background_jobs` row identified by `jobId` changes.
 *
 * Inputs:
 * - Dexie-like handle + job id.
 *
 * Outputs:
 * - Observable<BackgroundJob | null> -- emits `null` when the job is missing
 *   from Dexie (useful for rendering a disabled placeholder).
 *
 * Failure Handling:
 * - Errors are logged and the observable emits `null`.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function watchBackgroundJobById(
  db: DexieLike,
  jobId: string,
): Observable<BackgroundJob | null> {
  return liveQuery<BackgroundJob | null>(async () => findBackgroundJobById(db, jobId));
}
